import React, { useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import axios from 'axios';
import { selectModuleGroupData, selectLoading, fetchModuleGroup } from '../store/slices/modulegroupSlice';
import ModuleGroupDialog from './ModuleGroupDialog';
import { getSalesReactBaseUrl } from '../utilities/api';
import './CategoryMaster.css';

const API_URL = `${getSalesReactBaseUrl()}/api/server_masters`;

const ModuleGroupContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const moduleGroupData = useSelector(selectModuleGroupData);
    const loading = useSelector(selectLoading);

    const [statusFilter, setStatusFilter] = useState('Active');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [editGroup, setEditGroup] = useState(null);

    const activeCount = useMemo(() => moduleGroupData.filter((item) => item.status === 'Active').length, [moduleGroupData]);
    const inactiveCount = useMemo(() => moduleGroupData.filter((item) => item.status === 'Inactive' || item.status === 'InActive').length, [moduleGroupData]);

    const filteredData = useMemo(() => moduleGroupData.filter((item) => item.status === statusFilter), [moduleGroupData, statusFilter]);

    const reload = () => dispatch(fetchModuleGroup());

    const handleSaveRequest = (groupItem) => {
        const isEdit = !!groupItem.module_group_id;
        confirmDialog({
            message: isEdit ? 'Are you sure you want to update this module group?' : 'Are you sure you want to add this new module group?',
            header: isEdit ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleConfirmedSave(groupItem)
        });
    };

    const handleConfirmedSave = async (groupItem) => {
        const isEdit = !!groupItem.module_group_id;
        try {
            const endpoint = isEdit ? `${API_URL}/updateModuleGroup/${groupItem.module_group_id}` : `${API_URL}/addModuleGroup`;
            const res = await axios.post(endpoint, groupItem, {
                headers: { 'Content-Type': 'application/json' }
            });
            const msg = res.data?.message || (isEdit ? 'Module group updated successfully' : 'Module group added successfully');
            toast.current.show({ severity: 'success', summary: 'Success', detail: msg, life: 3000 });
            setDisplayDialog(false);
            setEditGroup(null);
            reload();
        } catch (err) {
            const detail = err?.response?.data?.message || err?.message || 'Save failed. Please try again.';
            toast.current.show({ severity: 'error', summary: 'Error', detail, life: 4000 });
        }
    };

    const statusBodyTemplate = (rowData) => <Tag value={rowData.status} severity={rowData.status === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => {
                setEditGroup(rowData);
                setDisplayDialog(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Module Group List</h3>
                    <div className="category-status-tabs">
                        <button className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`} onClick={() => setStatusFilter('Active')}>
                            <i className="pi pi-check-circle" />
                            Active <span className="tab-count">{activeCount}</span>
                        </button>
                        <button className={`status-tab-btn inactive-tab${statusFilter === 'Inactive' ? ' selected' : ''}`} onClick={() => setStatusFilter('Inactive')}>
                            <i className="pi pi-times-circle" />
                            Inactive <span className="tab-count">{inactiveCount}</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                            }}
                            placeholder="Search module groups..."
                            className="w-full"
                        />
                    </span>
                    <Button
                        label="Add Module Group"
                        icon="pi pi-plus"
                        className="p-button-primary"
                        onClick={() => {
                            setEditGroup(null);
                            setDisplayDialog(true);
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="sub-masters-table-wrap">
                    <DataTable
                        value={filteredData}
                        filters={filters}
                        globalFilterFields={['module_group_name', 'icon']}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        showGridlines
                        tableStyle={{ minWidth: '40rem' }}
                        className="p-datatable-sm sub-masters-table"
                        emptyMessage={`No ${statusFilter.toLowerCase()} module groups found`}
                    >
                        <Column field="module_group_id" header="Group ID" sortable style={{ width: '120px' }} />
                        <Column field="module_group_name" header="Group Name" sortable />
                        <Column field="sort_id" header="Sort ID" sortable style={{ width: '100px' }} />
                        <Column field="icon" header="Icon" sortable style={{ width: '160px' }} />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ width: '120px', textAlign: 'center' }} />
                        <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                    </DataTable>
                </div>
            )}

            <ModuleGroupDialog
                visible={displayDialog}
                groupData={editGroup}
                onHide={() => {
                    setDisplayDialog(false);
                    setEditGroup(null);
                }}
                onSave={handleSaveRequest}
            />
        </Card>
    );
};

export default ModuleGroupContent;
